"use client";
import React from "react";
import { Clock, CalendarDays, Percent } from "lucide-react";
import { BookingQRModal } from "components/common";
import { toSlotDiscount, SlotDiscount } from "services/campaignPackage/formatter";
import { useT } from "providers/I18nProvider";
import dayjs from "utils/day";

/**
 * "Khuyến mãi theo khung giờ" của CHÍNH shop đang xem.
 *
 * Trang chủ đã có dải khung giờ (SlotDiscount) nhưng trang chi tiết thì chưa,
 * nên khách vào thẳng trang shop không hề biết shop đang giảm giá giờ vắng.
 * Dùng lại toSlotDiscount của trang chủ để cách hiển thị giống hệt nhau.
 * Không có deal đang chạy → trả null, không để khối rỗng.
 */
const ShopSlotDiscounts: React.FC<{ packages?: any[] }> = ({ packages }) => {
  const t = useT();
  const [qrOpen, setQrOpen] = React.useState(false);

  // Dữ liệu đã được trang lấy sẵn ở máy chủ → chỉ cần chuẩn hoá để hiển thị.
  const deals = React.useMemo(
    () =>
      (packages || [])
        .map((p) => toSlotDiscount(p, t("home.newPrice")))
        .filter((d): d is SlotDiscount => !!d)
        // Giảm nhiều nhất lên trước
        .sort((a, b) => b.discountPct - a.discountPct),
    [packages], // eslint-disable-line react-hooks/exhaustive-deps
  );

  if (!deals.length) return null;

  // Card gọn cho sidebar cột phải (layout 2 cột chốt 30/09): mỗi deal một
  // dòng "khung giờ — %giảm", một nút đặt chung ở cuối thay vì nút mỗi card.
  return (
    <>
      <section className="rounded-2xl border border-orange-100 bg-[#fffbeb] p-4">
        <h2 className="!mb-1 flex items-center gap-1.5 text-[15px] font-extrabold text-orange-700">
          <Percent size={15} />
          {t("shopSlot.title")}
        </h2>
        <p className="!mb-2 text-xs text-gray-600">{t("shopSlot.desc")}</p>

        <div className="divide-y divide-dashed divide-orange-200">
          {deals.map((deal, idx) => (
            <div key={idx} className="py-2.5">
              <div className="flex items-center justify-between gap-2">
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-black01">
                  <Clock size={14} className="shrink-0 text-orange-500" />
                  {deal.timeRange}
                </span>
                <b className="text-sm font-extrabold text-orange-600">
                  {deal.discountLabel}
                </b>
              </div>
              <div className="mt-1 flex items-center gap-1.5 text-xs text-gray-500">
                <CalendarDays size={12} className="shrink-0 text-gray-400" />
                {deal.days}
                {deal.endDate
                  ? ` · ${t("shopSlot.until", {
                      date: dayjs(deal.endDate).format("DD/MM/YYYY"),
                    })}`
                  : null}
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={() => setQrOpen(true)}
          className="mt-3 w-full rounded-full bg-primary py-2.5 text-sm font-bold text-white transition-transform hover:scale-[1.02] active:scale-95"
        >
          {t("shopSlot.book")}
        </button>
      </section>

      <BookingQRModal open={qrOpen} onClose={() => setQrOpen(false)} />
    </>
  );
};

export default ShopSlotDiscounts;
